import { useMemo } from "react";
import Chart from "react-apexcharts";
import AkasSelect from "../../../components/AkasSelect";
import ChartState from "./ChartState";

const formatQuantity = (value, unitLabel = "") => {
  const amount = Number(value || 0);
  return `${amount.toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  })} ${unitLabel}`.trim();
};

const summaryDetails = (items = [], selector) =>
  items
    .filter(selector)
    .slice(0, 3)
    .map((item) => `${selector(item)} (${item.unit_label})`)
    .join(" · ");

const SummaryIcon = ({ type }) => {
  const paths = {
    total: (
      <>
        <path d="M4 7l8-4l8 4v10l-8 4l-8-4z" />
        <path d="M4 7l8 4l8-4M12 11v10" />
      </>
    ),
    material: (
      <>
        <path d="M4 19V9M10 19V5M16 19v-7M22 19H2" />
        <path d="M4 9l6-4l6 7l5-6" />
      </>
    ),
    category: (
      <>
        <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />
      </>
    ),
    count: (
      <>
        <path d="M8 7h12M8 12h12M8 17h12" />
        <path d="M4 7h.01M4 12h.01M4 17h.01" />
      </>
    ),
  };

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      {paths[type]}
    </svg>
  );
};

const UnitUsageChart = ({ group, chartColors, onDrillDown }) => {
  const rows = useMemo(() => group?.data || [], [group?.data]);
  const canDrillDown = group?.group_by === "category";
  const rowLabels = useMemo(
    () => rows.map((item) => (group?.group_by === "material" && item.category_label ? `${item.name}` : item.name)),
    [group?.group_by, rows],
  );
  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        fontFamily: "inherit",
        toolbar: { show: false },
        zoom: { enabled: false },
        parentHeightOffset: 0,
        events: {
          dataPointSelection: (_event, _context, config) => {
            if (!canDrillDown) return;
            const category = rows[config?.dataPointIndex];
            if (category?.category) onDrillDown?.(category.category);
          },
        },
      },
      colors: [chartColors.moneyOut],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: rows.length > 8 ? "58%" : "50%",
          borderRadius: 7,
          borderRadiusApplication: "end",
          dataLabels: { position: "center" },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (value) => formatQuantity(value, group.unit_label),
        style: { fontSize: "10px", fontWeight: 800, colors: ["#f8ffe9"] },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 0,
          blur: 2,
          color: "#06242a",
          opacity: 0.9,
        },
      },
      xaxis: {
        categories: rowLabels,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          rotate: rows.length > 6 ? -35 : 0,
          rotateAlways: rows.length > 6,
          hideOverlappingLabels: false,
          trim: false,
          style: { fontSize: rows.length > 6 ? "6px" : "10px", fontWeight: 650 },
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => formatQuantity(value, group.unit_label),
          style: { fontSize: "10px", fontWeight: 650 },
        },
      },
      tooltip: {
        y: { formatter: (value) => formatQuantity(value, group.unit_label) },
      },
      grid: {
        borderColor: chartColors.grid,
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 8, right: 12, left: 6 },
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
    }),
    [canDrillDown, chartColors.grid, chartColors.moneyOut, group.unit_label, onDrillDown, rowLabels, rows],
  );

  return (
    <section className={`dashboard-material-unit-chart ${canDrillDown ? "is-drillable" : ""}`}>
      <div className="dashboard-material-unit-heading">
        <div>
          <span>{group.unit_label}</span>
          <strong>{formatQuantity(group.total_usage, group.unit_label)}</strong>
        </div>
        <small>
          {group.material_count} material digunakan{canDrillDown ? " · Klik kategori untuk detail" : ""}
        </small>
      </div>
      <div className="dashboard-material-chart-scroll">
        <div
          className="dashboard-material-chart-canvas"
          style={{ "--material-chart-min-width": `${Math.max(620, rows.length * 84)}px` }}>
          <Chart
            options={options}
            series={[{ name: "Pemakaian", data: rows.map((item) => Number(item.usage || 0)) }]}
            type="bar"
            height={380}
          />
        </div>
      </div>
    </section>
  );
};

const MaterialUsageChart = ({
  data,
  loading,
  error,
  filters,
  rangeLabel,
  chartColors,
  onFilterChange,
  onDrillDown,
  onClearDrillDown,
}) => {
  const summary = data?.summary || {};
  const groups = data?.unit_groups || [];
  const byUnit = summary?.by_unit || [];
  const categoryOptions = [{ value: "all", label: "Semua kategori" }, ...(data?.filters?.categories || [])];
  const unitOptions = [{ value: "all", label: "Semua satuan" }, ...(data?.filters?.units || [])];
  const isSingleUnit = byUnit.length === 1;
  const totalDetail = byUnit.map((item) => formatQuantity(item.total_usage, item.unit_label)).join(" · ");
  const topMaterialDetail = summaryDetails(byUnit, (item) => item.top_material?.name);
  const topCategoryDetail = summaryDetails(byUnit, (item) => item.top_category?.name);

  return (
    <div className="card dashboard-card dashboard-material-usage-card-main">
      <div className="card-header dashboard-material-usage-header">
        <div>
          <span className="dashboard-table-eyebrow">PEMAKAIAN MATERIAL</span>
          <h3 className="card-title">Pemakaian Material</h3>
        </div>
        <span className="dashboard-material-period">{rangeLabel}</span>
      </div>

      <div className="card-body dashboard-material-usage-body">
        <div className="dashboard-material-filter-panel">
          <div className="dashboard-material-filter-field">
            <label className="form-label">Kategori Material</label>
            <AkasSelect
              value={filters.category}
              options={categoryOptions}
              onChange={(event) => onFilterChange("category", event.target.value)}
              isSearchable
              aria-label="Filter kategori material"
            />
          </div>
          <div className="dashboard-material-filter-field">
            <label className="form-label">Satuan</label>
            <AkasSelect
              value={filters.unit}
              options={unitOptions}
              onChange={(event) => onFilterChange("unit", event.target.value)}
              isSearchable
              aria-label="Filter satuan material"
            />
          </div>
          <div className="dashboard-material-filter-field dashboard-material-group-filter">
            <label className="form-label">Group by</label>
            <div
              className="akas-scope-filter dashboard-material-group-options"
              role="group"
              aria-label="Pengelompokan pemakaian material">
              {[
                { value: "material", label: "Material" },
                { value: "category", label: "Kategori Material" },
              ].map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={`btn ${filters.groupBy === option.value ? "btn-primary" : "btn-outline-secondary"}`}
                  aria-pressed={filters.groupBy === option.value}
                  onClick={() => onFilterChange("groupBy", option.value)}>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {data?.drill_down ? (
          <div className="dashboard-material-drilldown">
            <div>
              <span>DRILL-DOWN KATEGORI</span>
              <strong>{data.drill_down.category_label}</strong>
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={onClearDrillDown}>
              Kembali ke kategori
            </button>
          </div>
        ) : null}

        <div className="dashboard-material-summary-grid">
          <article>
            <span>
              <SummaryIcon type="total" />
            </span>
            <div>
              <small>Total pemakaian</small>
              <strong>
                {isSingleUnit
                  ? formatQuantity(summary.total_usage, summary.unit_label)
                  : `${summary.unit_count || 0} satuan aktif`}
              </strong>
              <p>{isSingleUnit ? "Pada periode terpilih" : totalDetail || "Belum ada pemakaian"}</p>
            </div>
          </article>
          <article>
            <span>
              <SummaryIcon type="material" />
            </span>
            <div>
              <small>Material paling banyak</small>
              <strong>{isSingleUnit ? summary.top_material?.name || "—" : "Teratas per satuan"}</strong>
              <p>
                {isSingleUnit
                  ? formatQuantity(summary.top_material?.usage, summary.unit_label)
                  : topMaterialDetail || "Belum ada material"}
              </p>
            </div>
          </article>
          <article>
            <span>
              <SummaryIcon type="category" />
            </span>
            <div>
              <small>Kategori tertinggi</small>
              <strong>{isSingleUnit ? summary.top_category?.name || "—" : "Teratas per satuan"}</strong>
              <p>
                {isSingleUnit
                  ? formatQuantity(summary.top_category?.usage, summary.unit_label)
                  : topCategoryDetail || "Belum ada kategori"}
              </p>
            </div>
          </article>
          <article>
            <span>
              <SummaryIcon type="count" />
            </span>
            <div>
              <small>Material digunakan</small>
              <strong>{Number(summary.material_count || 0).toLocaleString("id-ID")}</strong>
              <p>Material unik pada periode ini</p>
            </div>
          </article>
        </div>

        {error ? <div className="alert alert-warning mb-3">{error}</div> : null}
        <ChartState loading={loading} empty={groups.length === 0}>
          <div className={`dashboard-material-chart-grid ${groups.length === 1 ? "is-single" : ""}`}>
            {groups.map((group) => (
              <UnitUsageChart key={group.unit} group={group} chartColors={chartColors} onDrillDown={onDrillDown} />
            ))}
          </div>
        </ChartState>
      </div>
    </div>
  );
};

export default MaterialUsageChart;
